import { useCallback, useEffect, useRef } from 'react';
import { buildOAuthStartPath, type OAuthProvider } from '@/shared/config/routes';
import {
  OAUTH_CHANNEL,
  OAUTH_CLOSED_CHECK_DELAY_MS,
  OAUTH_POPUP_NAME,
  OAUTH_POPUP_SIZE,
} from '../constants';
import { parseOAuthMessage } from '../model/oauth-message';
import type { OAuthMessage } from '../types';

type UseOAuthPopupOptions = {
  /** The popup reported success, or closed while the session cookie got set. */
  onSignedIn: (provider: OAuthProvider) => void;
  onFailed: (provider: OAuthProvider) => void;
  /** Reads the session once when the popup closed without a message. */
  checkSignedIn: () => Promise<boolean>;
};

type UseOAuthPopupResult = {
  startOAuth: (provider: OAuthProvider) => boolean;
  cancelOAuth: () => void;
};

function popupFeatures(): string {
  const { width, height } = OAUTH_POPUP_SIZE;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  return `popup,width=${width},height=${height},left=${left},top=${top}`;
}

/**
 * Google or GitHub in a popup. The callback page posts on a BroadcastChannel and to the opener
 * (Google can sever window.opener with COOP); a popup closed without a message is checked against
 * the session once before it counts as "did not finish". Returns false when the popup was blocked
 * and the page went to the redirect flow instead.
 */
export function useOAuthPopup({
  onSignedIn,
  onFailed,
  checkSignedIn,
}: UseOAuthPopupOptions): UseOAuthPopupResult {
  const cleanupRef = useRef<() => void>(() => undefined);
  const handlers = useRef({ onSignedIn, onFailed, checkSignedIn });
  handlers.current = { onSignedIn, onFailed, checkSignedIn };

  useEffect(() => () => cleanupRef.current(), []);

  const cancelOAuth = useCallback(() => cleanupRef.current(), []);

  const startOAuth = useCallback((provider: OAuthProvider): boolean => {
    cleanupRef.current();
    const popup = window.open(
      buildOAuthStartPath(provider, 'popup'),
      OAUTH_POPUP_NAME,
      popupFeatures(),
    );
    if (!popup) {
      window.location.assign(buildOAuthStartPath(provider, 'redirect'));
      return false;
    }

    let isSettled = false;
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(OAUTH_CHANNEL);
    const settle = (message: OAuthMessage) => {
      if (isSettled) return;
      isSettled = true;
      cleanup();
      if (message.status === 'ok') handlers.current.onSignedIn(provider);
      else handlers.current.onFailed(provider);
    };
    const onWindowMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = parseOAuthMessage(event.data);
      if (message) settle(message);
    };
    const onChannelMessage = (event: MessageEvent) => {
      const message = parseOAuthMessage(event.data);
      if (message) settle(message);
    };
    // Reading popup.closed while Google's page (COOP) is open logs a browser error, so it is read
    // once, when this window gets focus back: closing the popup returns focus here. The short delay
    // lets the callback page's message arrive first.
    let focusCheck = 0;
    const onFocus = () => {
      window.clearTimeout(focusCheck);
      focusCheck = window.setTimeout(() => {
        if (isSettled || !popup.closed) return;
        void handlers.current.checkSignedIn().then((isSignedIn) => {
          settle({
            source: 'macswarm-oauth',
            status: isSignedIn ? 'ok' : 'cancelled',
            isNewAccount: false,
          });
        });
      }, OAUTH_CLOSED_CHECK_DELAY_MS);
    };

    function cleanup() {
      window.clearTimeout(focusCheck);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('message', onWindowMessage);
      channel?.removeEventListener('message', onChannelMessage);
      channel?.close();
      cleanupRef.current = () => undefined;
    }

    window.addEventListener('message', onWindowMessage);
    window.addEventListener('focus', onFocus);
    channel?.addEventListener('message', onChannelMessage);
    cleanupRef.current = () => {
      isSettled = true;
      cleanup();
      if (!popup.closed) popup.close();
    };
    return true;
  }, []);

  return { startOAuth, cancelOAuth };
}
